import { createSlice } from '@reduxjs/toolkit';
import { EngagementMeetingsState } from './engagement-conferences.state';
import { FetchEngagementConferences } from './thunks/fetch-engagement-conferences.thunk';
import { AddEngagementConference } from './thunks/add-engagement-conferences.thunk';
import { UpdateSupportMeeting } from './thunks/update-support-meeting.thunk';
import { CancelSupportMeeting } from './thunks/cancel-support-meeting.thunk';
import { FetchEngagementAvailabilityBlocks } from './thunks/fetch-engagement-availability-blocks.thunk';
import { FetchEngagementMeetingProposals } from './thunks/fetch-engagement-meeting-proposals.thunk';

const initialState: EngagementMeetingsState = {
    engagementMeetings: [],
    availabilityBlocks: [],
    meetingProposals: [],
    isLoading: false,
    status: 'succeeded',
    error: '',
};

const engagementConferencesSlice = createSlice({
    name: 'engagementMeetings',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(FetchEngagementConferences.pending, (state) =>
                setPendingState(state)
            )
            .addCase(FetchEngagementConferences.rejected, (state, action) =>
                setRejectedState(
                    state,
                    action.error.message ||
                        'Failed to fetch engagement meetings'
                )
            )
            .addCase(FetchEngagementConferences.fulfilled, (state, action) => {
                state.engagementMeetings = action.payload;
                setSucceededState(state);
            })

            .addCase(AddEngagementConference.pending, (state) => {
                state.error = '';
            })
            .addCase(AddEngagementConference.rejected, (state, action) => {
                state.isLoading = false;
                state.status = 'failed';
                state.error =
                    action.error.message || 'Failed to add engagement meeting';
            })
            .addCase(AddEngagementConference.fulfilled, (state, action) => {
                state.isLoading = false;
                state.status = 'succeeded';
                state.error = '';
            })

            .addCase(UpdateSupportMeeting.pending, (state) => {
                state.error = '';
            })
            .addCase(UpdateSupportMeeting.rejected, (state, action) =>
                setRejectedState(
                    state,
                    action.error.message || 'Failed to update support meeting'
                )
            )
            .addCase(UpdateSupportMeeting.fulfilled, (state) =>
                setSucceededState(state)
            )

            .addCase(CancelSupportMeeting.pending, (state) => {
                state.error = '';
            })
            .addCase(CancelSupportMeeting.rejected, (state, action) =>
                setRejectedState(
                    state,
                    action.error.message || 'Failed to cancel support meeting'
                )
            )
            .addCase(CancelSupportMeeting.fulfilled, (state) =>
                setSucceededState(state)
            )

            .addCase(FetchEngagementAvailabilityBlocks.pending, (state) =>
                setPendingState(state)
            )
            .addCase(
                FetchEngagementAvailabilityBlocks.rejected,
                (state, action) =>
                    setRejectedState(
                        state,
                        action.error.message ||
                            'Failed to fetch availability blocks'
                    )
            )
            .addCase(
                FetchEngagementAvailabilityBlocks.fulfilled,
                (state, action) => {
                    state.availabilityBlocks = action.payload;
                    setSucceededState(state);
                }
            )

            .addCase(FetchEngagementMeetingProposals.pending, (state) =>
                setPendingState(state)
            )
            .addCase(
                FetchEngagementMeetingProposals.rejected,
                (state, action) =>
                    setRejectedState(
                        state,
                        action.error.message ||
                            'Failed to fetch meeting proposals'
                    )
            )
            .addCase(
                FetchEngagementMeetingProposals.fulfilled,
                (state, action) => {
                    state.meetingProposals = action.payload;
                    setSucceededState(state);
                }
            );
    },
});

const setRejectedState = (
    state: EngagementMeetingsState,
    errorMessage: string
) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = errorMessage;
};

const setPendingState = (state: EngagementMeetingsState) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: EngagementMeetingsState) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};

export const {} = engagementConferencesSlice.actions;
export default engagementConferencesSlice.reducer;
